/**
 * Build the per-tenant highlight items shown on the landing page.
 *
 * The landing renders two highlight rows — one tucked next to the
 * upload form (form context: materials, variant axes, express, payment),
 * one below the hero (org-wide context: + shipping countries, payment
 * methods, EU residency). Both call this helper with a different
 * ``scope`` so the wording and the included items differ but the data
 * source is the same — the ``landing_facts`` payload returned by
 * ``GET /public/branding``.
 *
 * If a fact isn't configured (no priced materials, no shipping
 * carrier, Stripe Connect not enabled, …) the corresponding item is
 * silently dropped — we never render "0 materials" or "Shipping to "
 * placeholders.
 */

import type { TFunction } from "i18next";

import type { LandingFacts } from "@/api/queries/branding";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { minorToMajor } from "@/lib/money";
import { formatMoney } from "@/lib/utils";

export interface Highlight {
  key: string;
  icon: string;
  label: string;
}

export type HighlightScope = "form" | "public";

function formatCountries(codes: string[], t: TFunction): string {
  return codes
    .map((code) => t(`home.highlights.country.${code}`, { defaultValue: code }))
    .join(", ");
}

export function buildHighlights(
  facts: LandingFacts | null | undefined,
  t: TFunction,
  scope: HighlightScope,
  lang = "cs",
): Highlight[] {
  // Render nothing when we don't have tenant facts to back the claims
  // — neither the loading state (``undefined``) nor the platform
  // default shell (``null``, no per-tenant config). The previous
  // static fallback ("7 podporovaných materiálů", "Tloušťky 0,5–8 mm",
  // "Expres od 48 hodin", …) was a marketing lie on the platform
  // default tenant and stale data during loading.
  if (!facts) return [];
  const out: Highlight[] = [];

  if (scope === "public" && facts.material_count > 0) {
    const label =
      facts.material_names.length > 0
        ? t("home.highlights.materialsWithNames", {
            count: facts.material_count,
            names: facts.material_names.join(", "),
          })
        : t("home.highlights.materials", { count: facts.material_count });
    out.push({ key: "materials", icon: "▢", label });
  }

  // One highlight per priced numeric variant axis — label + unit come
  // from the declaring dimension row (BE), so no axis is named here.
  // Axis chips appear only in public scope.
  if (scope === "public") {
    // KAN-1291 — numbers in the page's locale: "0,5" on /cs, not "0.5".
    const number = new Intl.NumberFormat(lang);
    for (const [axisKey, range] of Object.entries(facts.dimension_ranges ?? {})) {
      const label = range.label_i18n[lang] || range.label_i18n.cs || axisKey;
      const unit = range.unit ? ` ${range.unit}` : "";
      out.push({
        key: `axis-${axisKey}`,
        icon: "↕",
        label: `${label} ${number.format(range.min)}–${number.format(range.max)}${unit}`,
      });
    }
  }

  // The fastest delivery tier the tenant actually configured, titled
  // with THAT tier's own name.
  //
  // This used to read ``express_dispatch_days`` — the same number — and
  // pair it with the fixed word "Expres". The number is honest (it is
  // the fastest tier's lead time); the word was not. A shop whose only
  // configured tier is "Standardní / min. 5 dní" advertised
  // "⚡ Expres do 5 dnů" — an express service nobody there had set up
  // (KAN-705). Taking the label from the tier makes the badge unable to
  // name a speed the tenant doesn't offer, whatever the tiers are called.
  const fastestTier = facts.delivery_tiers?.[0];
  // The chip promises DELIVERY, so it states ``delivery_days`` (production +
  // fastest transit); with transit unknown no chip beats a number that is
  // only the production floor (KAN-1803).
  const deliveryDays = fastestTier?.delivery_days ?? 0;
  if (fastestTier && deliveryDays >= 1) {
    const tierLabel = pickI18nBlob(
      fastestTier.label_i18n,
      fastestTier.label_cz,
      fastestTier.label_en,
      lang,
    );
    const label = t("home.highlights.tierDays", {
      label: tierLabel,
      count: deliveryDays,
    });
    out.push({ key: "express", icon: "⚡", label });
  }

  if (facts.payment_card) {
    out.push({
      key: "payment",
      icon: "✓",
      label: t("home.highlights.paymentCard"),
    });
  }

  // KAN-1669 — the top-up minimum, stated before the customer meets it as
  // "Dorovnání minimální objednávky" in the cart. Amount and currency are
  // the tenant's (same setting the cart reads); no minimum ⇒ no chip.
  if (scope === "public" && facts.minimum_order) {
    const { value_minor, currency_code } = facts.minimum_order;
    out.push({
      key: "minimum-order",
      icon: "≥",
      label: t("home.highlights.minimumOrderTopup", {
        amount: formatMoney(
          minorToMajor(value_minor, currency_code),
          currency_code,
          lang,
        ),
      }),
    });
  }

  if (scope === "public" && facts.shipping_country_codes.length > 0) {
    out.push({
      key: "shipping",
      icon: "→",
      label: t("home.highlights.shipping", {
        countries: formatCountries(facts.shipping_country_codes, t),
      }),
    });
  }

  // KAN-1938 — štítek „Data zpracovaná v EU" zrušen (rozhodnutí
  // vlastníka 2026-10-07): databáze a soubory v EU jsou, ale e-maily,
  // monitoring a hosting webu dodávají firmy z USA. Tvrzení bylo
  // silnější než zásady ochrany údajů.

  return out;
}
